import React, { useState, useEffect, useRef } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import DashboardMetricCard from '../components/common/DashboardMetricCard';
import DashboardChartCard from '../components/common/DashboardChartCard';
import DashboardReportHeader from '../components/common/DashboardReportHeader';
import { 
  Box, 
  Grid, 
  Alert,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import useMobileView from '../hooks/useMobileView';
import { getFinanceDashboardStatistics } from '../services/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
} from '../utils/insightsDashboardLayout';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const FinanceDashboardPage = () => {
  const { isCompact } = useMobileView();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // Date range state - default to the full current month
  const [startDate, setStartDate] = useState(moment().startOf('month'));
  const [endDate, setEndDate] = useState(moment().endOf('month'));

  // Data state
  const [statistics, setStatistics] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const reportRef = useRef(null);
  const exportInProgressRef = useRef(false);

  // Fetch data when date range changes
  useEffect(() => {
    if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
      fetchData();
    }
  }, [startDate, endDate]);

  const fetchData = async () => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return;
    }
    
    // Ensure start date is before or equal to end date
    if (startDate.isAfter(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await getFinanceDashboardStatistics(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      const normalizedPayments = data.payments || data.payment_list || [];
      const normalizedAnalytics = { ...(data.analytics || {}) };

      if (!normalizedAnalytics.future_bookings) {
        const futureBookings = data.future_bookings || [];
        normalizedAnalytics.future_bookings = {
          bookings: futureBookings,
          total_advance: data.future_advance_payments || 0,
          bookings_count: futureBookings.length,
        };
      }

      if (!normalizedAnalytics.revenue_loss_data) {
        normalizedAnalytics.revenue_loss_data = {
          total_loss: data.statistics?.revenue_loss || 0,
          cancelled_count: 0,
        };
      }

      if (!normalizedAnalytics.due_analysis) {
        const bookingsWithDue = data.bookings_with_due || [];
        normalizedAnalytics.due_analysis = {
          total_due: bookingsWithDue.reduce((sum, b) => sum + (Number(b?.due_amount) || 0), 0),
          due_percentage: data.statistics?.due_percentage || 0,
          bookings_with_due: bookingsWithDue,
        };
      }

      if (!normalizedAnalytics.payment_timing) {
        normalizedAnalytics.payment_timing = {
          before_checkin: {
            total: data.payments_before_checkin?.total || 0,
            count: (data.payments_before_checkin?.payments || []).length,
            payments: data.payments_before_checkin?.payments || [],
          },
          after_checkin: {
            total: data.payments_after_checkin?.total || 0,
            count: (data.payments_after_checkin?.payments || []).length,
            payments: data.payments_after_checkin?.payments || [],
          },
        };
      }
      if (!normalizedAnalytics.payment_method_breakdown) {
        normalizedAnalytics.payment_method_breakdown = data.payment_method_breakdown || [];
      }

      setStatistics(data.statistics || {});
      setDailyStats(data.daily_stats || []);
      setPayments(normalizedPayments);
      setAnalytics(normalizedAnalytics);
    } catch (err) {setError('Failed to load finance statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate) && newDate.isValid()) {
      setStartDate(newDate);
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate) && newDate.isValid()) {
      setEndDate(newDate);
    }
  };

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  };

  // Theme-aware colors
  const getThemeColors = () => {
    if (currentTheme === 'dark') {
      return {
        revenue: '#ff6b7d',
        expenses: '#f8e082',
        profit: '#66d4a8',
        cashflow: '#8ac9ff',
        income: '#4ce1b6',
        outflow: '#ff4861',
        advance: '#f6da6e',
        hotelDue: '#8e7dff',
        due: '#ff9f43',
      };
    } else {
      return {
        revenue: '#ff4861',
        expenses: '#f6da6e',
        profit: '#4ce1b6',
        cashflow: '#70bbfd',
        income: '#4ce1b6',
        outflow: '#ff4861',
        advance: '#f6da6e',
        hotelDue: '#8e7dff',
        due: '#ff9f43',
      };
    }
  };

  const themeColors = getThemeColors();
  const isDark = currentTheme === 'dark';
  const unifiedChartColors = isDark
    ? {
        red: '#ff6b7d',
        green: '#4ce1b6',
        yellow: '#f8e082',
      }
    : {
        red: '#ff4861',
        green: '#4ce1b6',
        yellow: '#f6da6e',
      };

  // Calculate totals from statistics
  const totalRevenue = statistics?.total_revenue || 0;
  const totalExpenses = statistics?.total_expense || 0;
  const totalHotelDue = statistics?.total_hotel_due || 0;
  const totalProfit = statistics?.total_profit || 0;
  const totalCollection = statistics?.total_collection || 0;
  const dueCollection = statistics?.due_collection || 0;

  // Prepare chart data for every date in the selected range, even when a day has no finance activity.
  const dailyStatsByDate = dailyStats.reduce((acc, stat) => {
    if (stat?.date) {
      acc[stat.date] = stat;
    }
    return acc;
  }, {});

  const getSelectedRangeDailyData = () => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    const allDates = [];
    const currentDate = startDate.clone().startOf('day');
    const lastDate = endDate.clone().startOf('day');

    while (currentDate.isSameOrBefore(lastDate)) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const stat = dailyStatsByDate[dateKey] || {};

      allDates.push({
        date: dateKey,
        name: currentDate.format('MMM DD'),
        revenue: stat.revenue || 0,
        expense: stat.expense || 0,
        hotelDue: stat.hotel_due || 0,
        profit: stat.profit || 0,
        dueCollection: stat.due_collection || 0,
        paymentMethods: stat.payment_methods || [],
        advance: stat.advance || 0,
        balance: stat.balance || 0,
      });

      currentDate.add(1, 'day');
    }

    return allDates;
  };

  const financeDailyData = getSelectedRangeDailyData();

  const revenueData = financeDailyData.map(stat => ({
    name: stat.name,
    value: stat.revenue || 0,
  }));

  const expensesData = financeDailyData.map(stat => ({
    name: stat.name,
    value: stat.expense || 0,
  }));

  const hotelDueData = financeDailyData.map(stat => ({
    name: stat.name,
    value: stat.hotelDue || 0,
  }));

  const profitData = financeDailyData.map(stat => ({
    name: stat.name,
    value: stat.profit || 0,
  }));

  // Finance statistics chart data (Revenue vs Expense)
  const statisticsData = financeDailyData.map(stat => ({
    name: stat.name,
    revenue: stat.revenue || 0,
    expense: stat.expense || 0,
    hotelDue: stat.hotelDue || 0,
    profit: stat.profit || 0,
  }));

  // Revenue loss from cancelled bookings
  const revenueLossData = analytics?.revenue_loss_data || { total_loss: 0, cancelled_count: 0 };

  // Payment method analysis for selected date range
  const paymentMethodBreakdown = analytics?.payment_method_breakdown || [];
  const paymentMethodTotals = {};
  const paymentMethodPalette = [
    '#4ce1b6',
    '#70bbfd',
    '#f6da6e',
    '#ff9f43',
    '#ff4861',
    '#8e7dff',
    '#26c6da',
    '#ef5350',
    '#66bb6a',
    '#ab47bc',
    '#ffa726',
    '#78909c',
  ];
  const paymentMethodColorMap = {
    Cash: '#4ce1b6',
    'Credit Card': '#ff4861',
    'Debit Card': '#70bbfd',
    'Bank Transfer': '#8e7dff',
    UPI: '#f6da6e',
    'Digital Wallet': '#26c6da',
    Cheque: '#ff9f43',
    Other: '#ab47bc',
    '—': '#78909c',
  };

  if (Array.isArray(paymentMethodBreakdown) && paymentMethodBreakdown.length > 0) {
    paymentMethodBreakdown.forEach((entry) => {
      const methodName = String(entry?.method || entry?.name || entry?.payment_method || '').trim();
      if (!methodName) return;
      paymentMethodTotals[methodName] = Number(entry?.value) || 0;
    });
  } else {
    payments.forEach((payment) => {
      const methodName = String(payment?.payment_method || payment?.method || '').trim();
      if (!methodName) return;
      const amount = Number(payment?.amount) || 0;
      paymentMethodTotals[methodName] = (paymentMethodTotals[methodName] || 0) + amount;
    });
  }

  const paymentMethodChartData = Object.entries(paymentMethodTotals).map(
    ([name, value], idx) => ({
      name,
      value,
      color: paymentMethodColorMap[name] || paymentMethodPalette[idx % paymentMethodPalette.length],
    })
  )
    .filter((item) => Number(item.value) > 0)
    .sort((a, b) => b.value - a.value);

  // Due amount analysis
  const dueAnalysis = analytics?.due_analysis || { total_due: 0, due_percentage: 0, bookings_with_due: [] };
  const dueChartData = dueAnalysis.bookings_with_due?.map(booking => ({
    name: booking.label || (booking.date ? moment(booking.date).format('MMM DD') : 'N/A'),
    due_amount: booking.due_amount || 0,
    due_percentage: booking.due_percentage || 0,
    booking_count: booking.booking_count || 0,
  })) || [];

  // Payment timing data (before vs after check-in)
  const paymentTiming = analytics?.payment_timing || {
    before_checkin: { total: 0, count: 0, payments: [] },
    after_checkin: { total: 0, count: 0, payments: [] },
  };
  
  const paymentTimingData = [
    {
      name: 'Before Check-in',
      value: paymentTiming.before_checkin?.total || 0,
      count: paymentTiming.before_checkin?.count || 0,
    },
    {
      name: 'After Check-in',
      value: paymentTiming.after_checkin?.total || 0,
      count: paymentTiming.after_checkin?.count || 0,
    },
  ];


  const formatCurrency = (val) => {
    if (typeof val === 'number') {
      return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return '₹0.00';
  };

  const formatPdfAmount = (val) =>
    Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleDownloadPdf = async () => {
    if (!statistics || loading || exportInProgressRef.current || !reportRef.current) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const filename = `finance-dashboard-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const rangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        onclone: (documentClone, clonedElement) => {
          documentClone.body.style.backgroundColor = '#ffffff';
          if (clonedElement) {
            clonedElement.style.backgroundColor = '#ffffff';
          }
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Finance Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional hotel finance intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader(pageIndex);
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter(pageIndex);
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      const formatPaymentBreakdown = (methods = []) => {
        const validMethods = (methods || []).filter((item) => Number(item?.value || 0) > 0);
        if (!validMethods.length) return '0.00';
        return validMethods
          .map((item) => `${item.method || item.name || '-'} - ${formatPdfAmount(item.value)}`)
          .join('\n');
      };
      const reportTableRows = financeDailyData.map((row) => [
        moment(row.date).format('DD MMM YYYY'),
        formatPdfAmount(row.revenue),
        formatPdfAmount(row.expense),
        formatPdfAmount(row.hotelDue),
        formatPdfAmount(row.profit),
        formatPdfAmount(row.dueCollection),
        formatPaymentBreakdown(row.paymentMethods),
      ]);

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Daily Finance Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Date-wise revenue, expenses, profit, due collection, and payment method breakdown.', margin, imageTop + 5);
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [['Date', 'Revenue', 'Hotel Expenses', 'Hotel Due', 'Profit', 'Due Collection', 'Payment Method']],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          styles: {
            font: 'helvetica',
            fontSize: 6.8,
            cellPadding: 1.4,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 24 },
            2: { cellWidth: 26 },
            3: { cellWidth: 30 },
            4: { cellWidth: 22 },
            5: { cellWidth: 28 },
            6: { cellWidth: 38 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index >= 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fontSize = 7.1;
              data.cell.styles.textColor = [15, 23, 42];
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, headerHeight, 'F');
            if (logoDataUrl) {
              pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
            }
            pdf.setTextColor(30, 41, 59);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.text('Finance Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(100, 116, 139);
            pdf.text(
              `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${currentPage}`,
              pageWidth / 2,
              23.6,
              { align: 'center' }
            );
            pdf.setDrawColor(226, 232, 240);
            pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
            drawPdfFooter(currentPage);
          },
        });
      }

      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  };

  const metricGrid = getInsightsMetricGridProps();
  const gridSpacing = getInsightsGridSpacing();

  return (
    <Box 
      sx={{ 
        p: 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Finance Dashboard"
          forceDark={isCompact}
          showFinanceSectionTabs
          navLinks={[
            { url: '/finance/reports/', label: 'Reports' },
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
      </Box>

      <DashboardReportHeader
        title="Finance dashboard insights"
        subtitle="Revenue, expenses, collections and payment behavior in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={fetchData}
        onPdfDownload={handleDownloadPdf}
        loading={loading}
        pdfExporting={pdfExporting}
        pdfDisabled={!statistics}
        themeMode={currentTheme}
        accentColor={themeColors.income}
      />

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!error && (loading || statistics) && (
        <Box
          ref={reportRef}
          sx={{
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          {/* Metric Cards */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Total Revenue"
                value={totalRevenue}
                unit="revenue"
                data={revenueData}
                color={themeColors.revenue}
                icon={<AttachMoneyIcon sx={{ color: themeColors.revenue }} />}
                theme={currentTheme}
                formatValue={formatCurrency}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Hotel expenses"
                value={totalExpenses}
                unit="hotel purchases"
                data={expensesData}
                color={themeColors.expenses}
                icon={<TrendingDownIcon sx={{ color: themeColors.expenses }} />}
                theme={currentTheme}
                formatValue={formatCurrency}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Hotel Due"
                value={totalHotelDue}
                unit="checkout due"
                data={hotelDueData}
                color={themeColors.hotelDue}
                icon={<TrendingDownIcon sx={{ color: themeColors.hotelDue }} />}
                theme={currentTheme}
                formatValue={formatCurrency}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Profit"
                value={totalProfit}
                unit="profit"
                data={profitData}
                color={themeColors.profit}
                icon={<TrendingUpIcon sx={{ color: themeColors.profit }} />}
                theme={currentTheme}
                formatValue={formatCurrency}
              />
            </Grid>
          </Grid>

          {/* Finance Statistics Chart - Revenue vs hotel expenses and hotel due */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item xs={12}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="FINANCE STATISTICS"
                title=""
                subtitle="Total Revenue vs hotel purchases and checkout due"
                data={statisticsData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'revenue', name: 'Total Revenue', color: themeColors.revenue },
                  { dataKey: 'expense', name: 'Hotel expenses', color: themeColors.expenses, stackId: 'expenses' },
                  { dataKey: 'hotelDue', name: 'Hotel Due', color: themeColors.hotelDue, stackId: 'expenses' },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
          </Grid>

          {/* Revenue Loss and Payment Method Analysis */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="REVENUE LOSS FROM CANCELLED BOOKINGS"
                title=""
                subtitle={`Total Loss: ${formatCurrency(revenueLossData.total_loss)} | Cancelled: ${revenueLossData.cancelled_count} bookings`}
                data={[{ name: 'Revenue Loss', value: revenueLossData.total_loss || 0 }]}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'value', name: 'Revenue Loss', color: themeColors.outflow },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="PAYMENT METHOD ANALYSIS"
                title=""
                subtitle={`Selected range: ${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')} | Total Collection: ${formatCurrency(totalCollection)}`}
                chartType="pie"
                pieData={paymentMethodChartData}
                pieAsBars
                pieInnerRadius={70}
                pieShowValueInLabel
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
              />
            </Grid>
          </Grid>

          {/* Due Amount Analysis and Payment Timing */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="DUE AMOUNT ANALYSIS"
                title=""
                subtitle={`Checked-out dues: ${formatCurrency(dueAnalysis.total_due)} | Due Percentage: ${dueAnalysis.due_percentage.toFixed(2)}%`}
                data={dueChartData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'due_amount', name: 'Due Amount', color: themeColors.due },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="PAYMENT TIMING ANALYSIS"
                title=""
                subtitle="Payments before vs after check-in"
                chartType="pie"
                pieData={paymentTimingData.map(item => ({
                  name: `${item.name} (${item.count})`,
                  value: item.value,
                  color: item.name === 'Before Check-in' ? themeColors.advance : themeColors.revenue,
                }))}
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
              />
            </Grid>
          </Grid>

        </Box>
      )}
    </Box>
  );
};

export default FinanceDashboardPage;
